/**
 * Agent dashboard: review and approve/reject tenant rent evidence for an assigned property.
 * Mirrors landlord behaviour; lives under Agent for role-specific structure and future tweaks.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getRentEvidence,
  approveRentEvidence,
  rejectRentEvidence,
} from '../../../api/services/propertyService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { storeImageInCache, getImageFromCache } from '../../../utils/imageCache';
import './RentEvidencePanel.css';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function formatSubmittedSummary(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function statusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING') return 'rent-evidence-status rent-evidence-status--pending';
  if (s === 'APPROVED') return 'rent-evidence-status rent-evidence-status--approved';
  if (s === 'REJECTED') return 'rent-evidence-status rent-evidence-status--rejected';
  return 'rent-evidence-status';
}

function isPending(row) {
  return String(row?.status || '').toUpperCase() === 'PENDING';
}

function listFromRentEvidenceResponse(res) {
  const raw = res?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.content)) return raw.content;
  return [];
}

const MONTH_OPTIONS = [{ value: '', label: 'Any month' }, ...MONTHS];

const RentEvidencePanel = ({ propertyId, units = [] }) => {
  const now = useMemo(() => new Date(), []);
  const defaultYear = now.getFullYear();

  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [status, setStatus] = useState('PENDING');
  const [unitId, setUnitId] = useState('');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [detailRow, setDetailRow] = useState(null);
  const [detailPhotoBlob, setDetailPhotoBlob] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  /** After "Reject" is clicked, show reason field + confirm/cancel (two-step reject). */
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [actionMode, setActionMode] = useState(null);
  const [actionError, setActionError] = useState('');

  const yearOptions = useMemo(() => {
    const y = defaultYear;
    const list = [{ value: '', label: 'Any year' }];
    for (let i = y - 2; i <= y + 1; i += 1) {
      list.push({ value: i, label: String(i) });
    }
    return list;
  }, [defaultYear]);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getRentEvidence(propertyId, {
        year: year === '' ? undefined : year,
        month: month === '' ? undefined : month,
        status: status || undefined,
        unitId: unitId || undefined,
      });
      setItems(listFromRentEvidenceResponse(res));
    } catch (e) {
      console.error(e);
      setError(e.message || 'Could not load rent evidence.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, year, month, status, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!detailRow?.photoUrl || !detailRow.photoUrl.startsWith('http')) {
      setDetailPhotoBlob(null);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const url = detailRow.photoUrl;
        const cached = getImageFromCache(url);
        if (cached) {
          if (!cancelled) setDetailPhotoBlob(cached);
          return;
        }
        const blobUrl = await fetchAuthenticatedImage(url);
        if (blobUrl) {
          await storeImageInCache(url, blobUrl);
          if (!cancelled) setDetailPhotoBlob(blobUrl);
        }
      } catch (err) {
        console.error('Rent evidence detail photo:', err);
        if (!cancelled) setDetailPhotoBlob(null);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [detailRow]);

  useEffect(() => {
    setRejectReasonOpen(false);
    setRejectReason('');
  }, [detailRow?.id]);

  const closeDetail = () => {
    setDetailRow(null);
    setDetailPhotoBlob(null);
    setRejectReason('');
    setRejectReasonOpen(false);
    setActionError('');
    setActionMode(null);
  };

  const cancelRejectFlow = () => {
    if (actionBusy) return;
    setRejectReasonOpen(false);
    setRejectReason('');
    setActionError('');
  };

  const actionBusy = actionMode !== null;

  const handleApprove = async () => {
    if (!detailRow || !propertyId) return;
    setActionMode('approve');
    setActionError('');
    try {
      await approveRentEvidence(propertyId, detailRow.id);
      closeDetail();
      await load();
    } catch (e) {
      setActionError(e.message || 'Could not approve.');
    } finally {
      setActionMode(null);
    }
  };

  const REJECT_REASON_INPUT_ID = 'agent-rent-evidence-reject-reason';

  const handleReject = async () => {
    if (!detailRow || !propertyId) return;
    setActionMode('reject');
    setActionError('');
    try {
      const el = typeof document !== 'undefined' ? document.getElementById(REJECT_REASON_INPUT_ID) : null;
      const fromDom = el && 'value' in el ? String(el.value).trim() : '';
      const fromState = String(rejectReason ?? '').trim();
      const reasonText = fromDom || fromState;
      await rejectRentEvidence(propertyId, detailRow.id, { rejectionReason: reasonText });
      closeDetail();
      await load();
    } catch (e) {
      setActionError(e.message || 'Could not reject.');
    } finally {
      setActionMode(null);
    }
  };

  const detailModal =
    detailRow &&
    createPortal(
      <div
        className="rent-evidence-modal-overlay"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget && !actionBusy) closeDetail();
        }}
      >
        <div
          className="rent-evidence-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-rent-evidence-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rent-evidence-modal__header">
            <h3 id="agent-rent-evidence-modal-title" className="rent-evidence-modal__title">
              Evidence details
            </h3>
            <button
              type="button"
              className="rent-evidence-modal__close"
              onClick={() => !actionBusy && closeDetail()}
              disabled={actionBusy}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="rent-evidence-modal__body">
            {detailRow.mpesaMessage ? (
              <div className="rent-evidence-modal__mpesa">
                <span className="rent-evidence-modal__section-label">M-Pesa message</span>
                <p className="rent-evidence-modal__mpesa-text">{detailRow.mpesaMessage}</p>
              </div>
            ) : null}

            {detailRow.photoUrl ? (
              <div className="rent-evidence-modal__photo-block">
                <span className="rent-evidence-modal__section-label">Photo</span>
                <div className="rent-evidence-modal__photo-wrap">
                  {detailPhotoBlob ? (
                    <img className="rent-evidence-modal__photo" src={detailPhotoBlob} alt="Payment evidence" />
                  ) : (
                    <span className="rent-evidence-modal__photo-placeholder">Loading image…</span>
                  )}
                </div>
              </div>
            ) : null}

            {!detailRow.mpesaMessage && !detailRow.photoUrl ? (
              <p className="rent-evidence-modal__empty">No M-Pesa message or photo for this submission.</p>
            ) : null}

            {actionError ? (
              <div className="rent-evidence-modal__error" role="alert">
                {actionError}
              </div>
            ) : null}

            {isPending(detailRow) ? (
              <>
                {rejectReasonOpen ? (
                  <>
                    <label className="rent-evidence-modal__reject-label" htmlFor={REJECT_REASON_INPUT_ID}>
                      Rejection reason <span className="rent-evidence-modal__optional">(optional)</span>
                    </label>
                    <textarea
                      id={REJECT_REASON_INPUT_ID}
                      name="rejectionReason"
                      className="rent-evidence-modal__textarea"
                      rows={3}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Explain why this evidence is rejected…"
                      disabled={actionBusy}
                    />
                    <div className="rent-evidence-modal__actions">
                      <button
                        type="button"
                        className="rent-evidence-modal__btn rent-evidence-modal__btn--cancel"
                        onClick={cancelRejectFlow}
                        disabled={actionBusy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="rent-evidence-modal__btn rent-evidence-modal__btn--reject"
                        onClick={handleReject}
                        disabled={actionBusy}
                      >
                        {actionMode === 'reject' ? 'Rejecting…' : 'Confirm rejection'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="rent-evidence-modal__actions">
                    <button
                      type="button"
                      className="rent-evidence-modal__btn rent-evidence-modal__btn--reject"
                      onClick={() => {
                        setRejectReasonOpen(true);
                        setActionError('');
                      }}
                      disabled={actionBusy}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="rent-evidence-modal__btn rent-evidence-modal__btn--approve"
                      onClick={handleApprove}
                      disabled={actionBusy}
                    >
                      {actionMode === 'approve' ? 'Approving…' : 'Approve'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="rent-evidence-modal__readonly">This submission is already {String(detailRow.status || '').toLowerCase()}.</p>
            )}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="rent-evidence-panel">
      <div className="rent-evidence-panel__intro">
        <h2 className="rent-evidence-panel__title">Rent evidence</h2>
      </div>

      <div className="rent-evidence-filters" role="search" aria-label="Filter rent evidence">
        <div className="rent-evidence-filters__grid">
          <div className="rent-evidence-field">
            <label htmlFor="agent-rent-evidence-year">Year</label>
            <select
              id="agent-rent-evidence-year"
              className="rent-evidence-select"
              value={year === '' ? '' : String(year)}
              onChange={(e) => {
                const v = e.target.value;
                setYear(v === '' ? '' : Number(v));
              }}
            >
              {yearOptions.map((opt) => (
                <option key={opt.value === '' ? 'any' : opt.value} value={opt.value === '' ? '' : String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="rent-evidence-field">
            <label htmlFor="agent-rent-evidence-month">Month</label>
            <select
              id="agent-rent-evidence-month"
              className="rent-evidence-select"
              value={month === '' ? '' : String(month)}
              onChange={(e) => {
                const v = e.target.value;
                setMonth(v === '' ? '' : Number(v));
              }}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value === '' ? 'any' : m.value} value={m.value === '' ? '' : String(m.value)}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="rent-evidence-field">
            <label htmlFor="agent-rent-evidence-status">Status</label>
            <select
              id="agent-rent-evidence-status"
              className="rent-evidence-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="rent-evidence-field rent-evidence-field--span">
            <label htmlFor="agent-rent-evidence-unit">Unit</label>
            <select
              id="agent-rent-evidence-unit"
              className="rent-evidence-select"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
            >
              <option value="">All units</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {[u.unitNumber, u.name].filter(Boolean).join(' · ') || u.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rent-evidence-error" role="alert">
          {error}
        </div>
      )}

      {loading && !items.length && !error ? (
        <div className="rent-evidence-empty">Loading rent evidence…</div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <div className="rent-evidence-empty">No rent evidence matches these filters.</div>
      ) : null}

      <ul className="rent-evidence-summary-grid">
        {items.map((row) => {
          const periodLabel = `${MONTHS.find((m) => m.value === row.periodMonth)?.label || `Month ${row.periodMonth}`} ${row.periodYear}`;
          const unitLine = [row.unitNumber, row.unitName].filter(Boolean).join(' · ') || '—';
          const amount = `KES ${Number(row.amountPaid ?? 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;

          return (
            <li key={row.id} className="rent-evidence-summary-card">
              <div className="rent-evidence-summary-card__top">
                <span className={statusClass(row.status)}>{row.status || '—'}</span>
              </div>
              <div className="rent-evidence-summary-card__body">
                <div className="rent-evidence-summary-kv">
                  <span className="rent-evidence-summary-kv__label">Unit</span>
                  <span className="rent-evidence-summary-kv__value">{unitLine}</span>
                </div>
                <div className="rent-evidence-summary-kv">
                  <span className="rent-evidence-summary-kv__label">Period</span>
                  <span className="rent-evidence-summary-kv__value">{periodLabel}</span>
                </div>
                <div className="rent-evidence-summary-kv">
                  <span className="rent-evidence-summary-kv__label">Amount paid</span>
                  <span className="rent-evidence-summary-kv__value rent-evidence-summary-kv__value--amount">{amount}</span>
                </div>
                <div className="rent-evidence-summary-kv">
                  <span className="rent-evidence-summary-kv__label">Submitted</span>
                  <span className="rent-evidence-summary-kv__value rent-evidence-summary-kv__value--muted">
                    {formatSubmittedSummary(row.createdAt)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="rent-evidence-summary-card__details-btn"
                onClick={() => {
                  setDetailRow(row);
                  setRejectReason('');
                  setActionError('');
                }}
              >
                View details
              </button>
            </li>
          );
        })}
      </ul>

      {detailModal}
    </div>
  );
};

export default RentEvidencePanel;
